import { Flex, FormControl, Switch, Text, useToast } from '@chakra-ui/react'
import { FaMinusCircle } from 'react-icons/fa'
import { ButtonCmp } from '../../../../../components/ButtonCmp'
import { InputCmp } from '../../../../../components/InputCmp'
import { TextAreaCmp } from '../../../../../components/TextareaCmp'
import { PropInvalida } from '../../../../../Utils/Helper'
import { iAcao } from '../TabPDI'

interface iAcaoProps {
  acao: iAcao
  onRemove: (id: string) => void
  onUpdate: (acao: iAcao) => void
  modoAprovacao?: boolean
  bloquearEdicao?: boolean
  onAceitarTodas?: (acao: iAcao) => void
  onResolver?: (acao: iAcao, campo: Campo, aceitar: boolean) => void
}

interface iDestaqueProps {
  ativo: boolean
  antes?: string
  depois?: string
  travado?: boolean
  onAceitar?: () => void
  onRecusar?: () => void
  children: React.ReactNode
}

const Destaque: React.FC<iDestaqueProps> = ({ ativo, antes, depois, travado, onAceitar, onRecusar, children }) => {
  const alterado = ativo && (antes ?? '') !== (depois ?? '')
  const toast = useToast()

  function avisarConflito(): void {
    if (toast.isActive('conflito-alteracao')) return
    toast({
      id: 'conflito-alteracao',
      title: 'O colaborador solicitou alterações nesta ação. Aceite ou recuse todas as alterações antes de editar.',
      status: 'warning',
      isClosable: false,
      position: 'top-right',
      duration: 3000
    })
  }

  return (
    <Flex flexDir={'column'} gap={'.25rem'} width={'100%'} padding={alterado ? '.375rem' : '0'} border={alterado ? '2px solid var(--Red)' : 'none'} borderRadius={'.25rem'}>
      <Flex flexDir={'column'} onKeyDownCapture={(alterado || travado) ? avisarConflito : undefined} onMouseDownCapture={(alterado || travado) ? avisarConflito : undefined}>
        {children}
      </Flex>
      {alterado && (
        <Flex justifyContent={'space-between'} alignItems={'center'} gap={'.5rem'}>
          <Text fontSize={'.75rem'} color={'var(--Gray2)'}>Valor atual: {PropInvalida(antes) ? '(vazio)' : antes}</Text>
          <Flex gap={'.5rem'}>
            {onAceitar && <ButtonCmp small VarColor='Green2' onClick={onAceitar}>Aceitar</ButtonCmp>}
            {onRecusar && <ButtonCmp small VarColor='Red' onClick={onRecusar}>Recusar</ButtonCmp>}
          </Flex>
        </Flex>
      )}
    </Flex>
  )
}

export type Campo = 'hora' | 'dataInicio' | 'dataFim' | 'titulo' | 'descricao' | 'assunto'

export const Acao: React.FC<iAcaoProps> = ({ acao, onRemove, onUpdate, modoAprovacao, bloquearEdicao, onResolver, onAceitarTodas }) => {
  const ativa = modoAprovacao ? acao.versaoAtiva : undefined
  const comparar = !!ativa
  const bloqueado = (campo: Campo): boolean => !modoAprovacao || !!bloquearEdicao || (comparar && (ativa?.[campo] ?? '') !== (acao.servidor?.[campo] ?? ''))

  function props(campo: Campo): Pick<iDestaqueProps, 'ativo' | 'antes' | 'depois' | 'travado' | 'onAceitar' | 'onRecusar'> {
    return {
      ativo: comparar,
      antes: ativa?.[campo],
      depois: acao.servidor?.[campo],
      travado: !!modoAprovacao && !!bloquearEdicao,
      onAceitar: () => onResolver?.(acao, campo, true),
      onRecusar: () => onResolver?.(acao, campo, false)
    }
  }

  return (
    <Flex gap={'1rem'} flexDir={'column'} borderRadius={'.25rem'} padding={'1rem'} border={'1px solid var(--c4)'}>
      <Flex justifyContent={'space-between'} alignItems={'center'}>
        {(modoAprovacao && bloquearEdicao) && (
          <Text fontSize={'.875rem'} fontWeight={'700'}>
            Aceite ou recuse todas as solicitações feitas pelo colaborador
          </Text>
        )}
        {!modoAprovacao && (
          <Flex alignItems={'center'} gap={'.5rem'}>
            <Text fontWeight={'500'}>Solicitar ajustes</Text>
            <Switch
              onChange={(e) => onUpdate({ ...acao, solicitarAjustes: e.target.checked })}
              isChecked={acao.solicitarAjustes}
            />
          </Flex>
        )}
        <Flex alignItems={'center'} gap={'.75rem'} marginLeft={'auto'}>
          {(modoAprovacao && bloquearEdicao && onAceitarTodas) && <ButtonCmp VarColor='c6' onClick={() => onAceitarTodas(acao)}>Aceitar todas as alterações</ButtonCmp>}
          <FaMinusCircle onClick={() => onRemove(acao.id)} cursor={'pointer'} size={20} color='var(--terc3)' />
        </Flex>
      </Flex>
      <Flex flexDir={'column'} gap={'.75rem'}>
        <Flex gap={'.75rem'}>
          <FormControl>
            <Destaque {...props('assunto')}>
              <InputCmp
                label='Assunto a ser tratado'
                isReadOnly
                value={acao.assunto}
                borderColor={'var(--Gray4)'}
                OnChange={() => {}}
              />
            </Destaque>
          </FormControl>
          <FormControl>
            <Destaque {...props('hora')}>
              <InputCmp
                label='Horas a dedicar'
                isReadOnly={bloqueado('hora')}
                marginRight={'.5rem'}
                type={'number'}
                borderColor={'var(--Gray4)'}
                value={acao.hora}
                OnChange={(e) => onUpdate({ ...acao, hora: e })}
              />
            </Destaque>
          </FormControl>
        </Flex>
        <Flex gap={'.75rem'}>
          <FormControl>
            <Destaque {...props('dataInicio')}>
              <InputCmp
                label='Data para iniciar'
                isReadOnly={bloqueado('dataInicio')}
                type={'date'}
                borderColor={'var(--Gray4)'}
                max={'2099-12-31T23:59'}
                value={acao.dataInicio}
                OnChange={(e) => onUpdate({ ...acao, dataInicio: e })}
              />
            </Destaque>
          </FormControl>
          <FormControl>
            <Destaque {...props('dataFim')}>
              <InputCmp
                label='Data para finalizar'
                isReadOnly={bloqueado('dataFim')}
                type={'date'}
                borderColor={'var(--Gray4)'}
                max={'2099-12-31T23:59'}
                value={acao.dataFim}
                OnChange={(e) => onUpdate({ ...acao, dataFim: e })}
              />
            </Destaque>
          </FormControl>
        </Flex>
      </Flex>

      <Destaque {...props('titulo')}>
        <Flex flexDir={'column'} fontSize={'.875rem'}>
          <Text fontWeight='700'>O que vou fazer?</Text>
          <InputCmp isReadOnly={bloqueado('titulo')} OnChange={(e) => onUpdate({ ...acao, titulo: e })} value={acao.titulo}/>
        </Flex>
      </Destaque>

      <Destaque {...props('descricao')}>
        <Flex flexDir={'column'} fontSize={'.875rem'}>
          <Text fontWeight='700'>Como será realizada essa ação</Text>
          <TextAreaCmp isReadOnly={bloqueado('descricao')} OnChange={(e) => onUpdate({ ...acao, descricao: e })} value={acao.descricao} height={'10rem'}/>
        </Flex>
      </Destaque>
      <FormControl>
        <TextAreaCmp label='Deixe uma observação para seu colaborador' OnChange={(e) => {
          onUpdate({ ...acao, comentario: e })
        }} borderColor={'var(--Gray4)'} />
      </FormControl>
    </Flex>
  )
}
